// Implements: F-UIX-021, F-VER-006, F-VER-014, F-UIX-007, F-UIX-010, F-USG-006
/**
 * The version chip of the Lienzo editor's topbar (UX-05, the mockup's `#ver-chip`): "vN ▾" names the
 * current version and opens the versions, newest first, in a popover.
 *
 * Each version can be looked at in the canvas ("Ver", the page shows it with a banner), used as the
 * current one ("Usar esta versión": the existing promote, a pointer move that creates no new version,
 * F-VER-006; the server decides and its refusal is read out) or compared with the current one INSIDE
 * the editor ("Comparar": the page draws both side by side; owner report, round 3). The tree and the pointer come from the queries the page already shares;
 * nothing here is optimistic. A mockup with no version says so and opens nothing.
 *
 * Mockup v3 P7: what each version cost ("USD 0.12 · 3 min", or "Sin coste · restaurada" for one no
 * run produced) is the chip's tooltip for the current version and a line of each timeline row, read
 * in ONE request for every row (chunked by 50 in `shared/api/versionCosts`), and only for whoever
 * may edit the mockup (P7-1). A cost not read yet, or refused, draws nothing and never holds the
 * chip back.
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";

import { fetchMockupDetail } from "../../../shared/api/mockupDetail";
import { mockupDetailKey } from "../../../shared/api/queryKeys";
import { useEditorVersionCosts } from "../../../shared/api/versionCosts";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { costLabel } from "../../../shared/ui/costText";
import { ChipPopover } from "../../../shared/ui/molecules/ChipPopover";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { VersionTimelineRow } from "../components/VersionTimelineRow";
import { useContinueFromVersion } from "../hooks/useContinueFromVersion";
import { usePromoteVersion } from "../hooks/usePromoteVersion";
import { useVersionNotes } from "../hooks/useVersionNotes";
import { useVersionTree } from "../hooks/useVersionTree";
import styles from "./VersionChipContainer.module.css";
import { VersionNoteContainer } from "./VersionNoteContainer";

export interface VersionChipContainerProps {
  readonly mockupId: string;
  /** The version the canvas shows instead of the current one, if any. */
  readonly previewing: string | null;
  /** Show this version in the canvas; `null` goes back to the current one. */
  readonly onPreview: (versionId: string | null) => void;
  /** Compare this version with the current one, side by side, in the editor. */
  readonly onCompare: (versionId: string) => void;
  /** The chip's own id, so the page can give it the focus back. */
  readonly triggerId?: string | undefined;
}

export function VersionChipContainer({
  mockupId,
  previewing,
  onPreview,
  onCompare,
  triggerId,
}: VersionChipContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const tree = useVersionTree(mockupId);
  const promote = usePromoteVersion();
  const continueFrom = useContinueFromVersion();
  const notes = useVersionNotes(mockupId).data;
  // The same answer the tree reads its pointer from: who may write here annotates (F-VER-008).
  const role = useQuery({ queryKey: mockupDetailKey(mockupId), queryFn: () => fetchMockupDetail(mockupId) }).data
    ?.your_role;
  const mayAnnotate = role === "owner" || role === "editor";
  const [continuing, setContinuing] = useState<string | null>(null);
  const { push } = useToast();
  const [promoting, setPromoting] = useState<string | null>(null);
  const versionIds = useMemo(
    () => (tree.status === "success" ? tree.data.nodes.map((node) => node.id) : []),
    [tree],
  );
  const costs = useEditorVersionCosts(mockupId, versionIds);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  if (tree.status === "pending") return null;
  if (tree.status === "error") {
    return <ErrorState error={tree.error} onRetry={tree.refetch} />;
  }
  const nodes = [...tree.data.nodes].sort((left, right) => right.sequence_number - left.sequence_number);
  const current = nodes.find((node) => node.id === tree.data.currentVersionId);
  if (current === undefined) {
    return <span className={styles.none}>{t("editor.versions.none")}</span>;
  }

  const currentCost = costLabel(costs.costs?.get(current.id), "chip");

  const use = async (versionId: string, close: () => void): Promise<void> => {
    if (promoting !== null) return;
    setPromoting(versionId);
    const refusal = await promote(mockupId, versionId);
    if (!alive.current) return;
    setPromoting(null);
    if (refusal === null) {
      push({ tone: "success", message: t("version.promoted") });
      onPreview(null);
      close();
    } else {
      push({ tone: "danger", message: userMessage(refusal, t("version.promote.failed")) });
    }
  };

  const resume = async (versionId: string, close: () => void): Promise<void> => {
    if (continuing !== null) return;
    setContinuing(versionId);
    const refusal = await continueFrom(mockupId, versionId);
    if (!alive.current) return;
    setContinuing(null);
    if (refusal === null) {
      push({ tone: "success", message: t("version.continued") });
      onPreview(null);
      close();
    } else {
      push({ tone: "danger", message: userMessage(refusal, t("version.continue.failed"), ["CONFLICT"]) });
    }
  };

  return (
    <ChipPopover
      label={t("editor.versions.chip", { n: current.sequence_number })}
      title={t("versions.title")}
      description={t("versions.title")}
      width="md"
      align="end"
      hint={currentCost === null ? undefined : t("cost.chipTitle", { n: current.sequence_number, cost: currentCost })}
      triggerId={triggerId}
    >
      {(close) => (
        <>
          <p className={styles.help}>
            <span>
              <strong>{t("versions.help.useLead")}</strong> {t("versions.help.useRest")}
            </span>
            <span>
              <strong>{t("versions.help.continueLead")}</strong> {t("versions.help.continueRest")}
            </span>
          </p>
          <ul className={styles.list}>
            {nodes.map((node) => {
              const isCurrent = node.id === current.id;
              const shown = previewing === null ? isCurrent : previewing === node.id;
              const cost = costLabel(costs.costs?.get(node.id), "chip");
              return (
                <VersionTimelineRow
                  key={node.id}
                  sequenceNumber={node.sequence_number}
                  createdAt={node.created_at}
                  isCurrent={isCurrent}
                  shown={shown}
                  note={
                    <VersionNoteContainer
                      key={node.id}
                      mockupId={mockupId}
                      versionId={node.id}
                      sequenceNumber={node.sequence_number}
                      note={notes?.get(node.id) ?? null}
                      editable={mayAnnotate}
                    />
                  }
                  cost={
                    cost === null ? null : (
                      <p className={styles.cost} data-version-cost="">
                        {cost}
                      </p>
                    )
                  }
                  actions={
                    isCurrent ? null : (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          label={t("editor.versions.previewOf", { n: node.sequence_number })}
                          onClick={() => {
                            onPreview(node.id);
                            close();
                          }}
                        >
                          <Icon name="eye" size="sm" />
                          {t("preview.tabs.preview")}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          label={t("editor.versions.compareWith", { n: node.sequence_number })}
                          onClick={() => {
                            onCompare(node.id);
                            close();
                          }}
                        >
                          <Icon name="compare" size="sm" />
                          {t("editor.versions.compare")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={promoting === node.id}
                          disabled={promoting !== null && promoting !== node.id}
                          onClick={() => {
                            void use(node.id, close);
                          }}
                        >
                          <Icon name="restore" size="sm" />
                          {t("editor.versions.use")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          label={t("versions.row.continueOf", { n: node.sequence_number })}
                          loading={continuing === node.id}
                          disabled={continuing !== null && continuing !== node.id}
                          onClick={() => {
                            void resume(node.id, close);
                          }}
                        >
                          <Icon name="history" size="sm" />
                          {t("versions.row.continue")}
                        </Button>
                      </>
                    )
                  }
                />
              );
            })}
          </ul>
        </>
      )}
    </ChipPopover>
  );
}
